import { useEffect, useState, useMemo } from 'react';
import { allColumns, essentialColumns } from './CustomCultureDataGridUtils';
import useFilteredCulturesLocalStorage from './useFilteredCulturesLocalStorage';

const useDataGridCultureVisibility = (rows, setRows, processedRows, from, user, organicState) => {
  const [isLargeGrid, setIsLargeGrid] = useState(false);
  const [columnVisible, setColumnVisible] = useState(allColumns);
  const [toggleVisibilityFilter, setToggleVisibilityFilter] = useState({
    conventional: false,
    organic: false,
  });
  const [selectedRows, setSelectedRows] = useState({
    conventional: { type: 'include', ids: new Set() },
    organic: { type: 'include', ids: new Set() },
  });
  const filteredCultures = JSON.parse(localStorage.getItem('filteredCultures'));
  let userMaskedCulturesIndex = -1;
  const structureId = Number(localStorage.getItem('cooperative') ?? user?.cooperative.id);
  const sectorId = Number(localStorage.getItem('sector'));
  const exploitationId = Number(localStorage.getItem('exploitation'));
  const [hasToggleCultureShow, setHasToggleCultureShow] = useState(false);

  if (filteredCultures) {
    switch (from) {
      case 'fromCooperative':
        userMaskedCulturesIndex = filteredCultures.maskedCulturesByLevel.findIndex(
          (obj) => obj?.structure === structureId
        );
        break;
      case 'fromSector':
        userMaskedCulturesIndex = filteredCultures.maskedCulturesByLevel.findIndex(
          (obj) => obj?.sector === sectorId
        );
        break;
      case 'fromExploitation':
        userMaskedCulturesIndex = filteredCultures.maskedCulturesByLevel.findIndex(
          (obj) => obj?.exploitation === exploitationId
        );
        break;
    }
    if (
      filteredCultures?.maskedCulturesByLevel?.[userMaskedCulturesIndex]?.maskedRows?.[organicState]
        ?.ids?.length > 0
    ) {
      filteredCultures.maskedCulturesByLevel[userMaskedCulturesIndex].maskedRows[organicState].ids =
        new Set(
          filteredCultures.maskedCulturesByLevel[userMaskedCulturesIndex].maskedRows[
            organicState
          ].ids
        );
    }
  }
  const [lastSelectedRows, setLastSelectedRows] = useState({
    conventional:
      filteredCultures && userMaskedCulturesIndex !== -1
        ? structuredClone(
            filteredCultures?.maskedCulturesByLevel[userMaskedCulturesIndex]?.maskedRows
              ?.conventional
          ) || new Set([])
        : new Set([]),
    organic:
      filteredCultures && userMaskedCulturesIndex !== -1
        ? structuredClone(
            filteredCultures?.maskedCulturesByLevel[userMaskedCulturesIndex]?.maskedRows?.organic
          ) || new Set([])
        : new Set([]),
  });

  useFilteredCulturesLocalStorage(
    user,
    from,
    filteredCultures,
    userMaskedCulturesIndex,
    lastSelectedRows,
    setLastSelectedRows,
    toggleVisibilityFilter,
    setToggleVisibilityFilter,
    selectedRows,
    structureId,
    sectorId,
    exploitationId,
    organicState,
    hasToggleCultureShow,
    setHasToggleCultureShow
  );

  useEffect(() => {
    const newColumns = isLargeGrid ? allColumns : essentialColumns;
    setColumnVisible(newColumns);
  }, [isLargeGrid]);

  const handleVisibility = () => {
    setIsLargeGrid(!isLargeGrid);
  };

  const filterCheckedRows = () => {
    let value = structuredClone(selectedRows);
    value[organicState].ids = new Set(value[organicState].ids);
    setSelectedRows(value);
    setLastSelectedRows((prev) => ({
      ...prev,
      [organicState]: structuredClone(value?.[organicState]),
    }));
    setRows(rows?.filter((culture) => !value?.[organicState]?.ids?.has(+culture.id)));
  };

  const handleCulturesHide = () => {
    setHasToggleCultureShow(false);
    if (selectedRows?.[organicState]?.ids?.size > 0) {
      setToggleVisibilityFilter({
        ...toggleVisibilityFilter,
        [organicState]: !toggleVisibilityFilter?.[organicState],
      });
      filterCheckedRows();
    }
  };

  const handleCulturesShow = () => {
    setHasToggleCultureShow(true);
    setToggleVisibilityFilter({
      ...toggleVisibilityFilter,
      [organicState]: !toggleVisibilityFilter?.[organicState],
    });
    setRows(processedRows);
    if (lastSelectedRows) {
      setSelectedRows((prev) => ({
        ...prev,
        [organicState]: lastSelectedRows?.[organicState],
      }));
    }
  };

  const badgeContent = Math.abs(lastSelectedRows?.[organicState]?.ids?.size?.toString());

  return {
    toggleVisibilityFilter,
    columnVisible,
    selectedRows,
    setSelectedRows,
    setColumnVisible,
    handleVisibility,
    handleCulturesHide,
    handleCulturesShow,
    badgeContent,
    isLargeGrid,
    lastSelectedRows,
  };
};

export default useDataGridCultureVisibility;
